import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import {
  deriveAddonRuleQuality,
  formatPeriodRange,
  type AddonAnalyticsReadRow,
  type AddonRuleQualityView,
} from '@/utils/addonRuleQuality';

/**
 * CORE-BP item 5 — the shop-facing rule-quality surface.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IT READS, AND WHY THAT IS THE WHOLE SAFETY ARGUMENT
 * ═══════════════════════════════════════════════════════════════════════════
 * `addon_suggestion_analytics` and nothing else. That table has no person
 * dimension — shop, suggestion type, period, counts — and after item 9 the raw
 * events it is built from have none either, and cannot be read through
 * PostgREST at all.
 *
 * So this is not "an advisor column we chose not to render". There is no
 * advisor in the data. That distinction is the point of item 9, and it is why
 * this component can be a plain read without a privacy review of every future
 * change to it.
 *
 * The shop id comes from the authenticated active shop and is used only as a
 * filter; the actual authority is the RLS policy
 * `is_manager_or_admin(auth.uid(), shop_id)`. A tampered id returns nothing
 * rather than someone else's shop.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THREE STATES, BECAUSE THEY ARE THREE DIFFERENT CLAIMS
 * ═══════════════════════════════════════════════════════════════════════════
 * "No outcome data yet" is not "0% acceptance", and neither is "we could not
 * read it". Every shop is in the first state today, because the aggregation
 * has only just been scheduled — rendering 0% would tell them their rules are
 * being refused when nothing has been measured at all.
 */
export function AddonRuleQualityPanel({ shopId }: { shopId: string | undefined }) {
  const [view, setView] = useState<AddonRuleQualityView | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!shopId) { setView({ state: 'empty' }); return; }

    (async () => {
      // Already-aggregated, person-free rows. Raw events are deliberately NOT
      // downloaded and summed here — they are not readable, and doing the
      // arithmetic in a browser is how a person dimension creeps back in.
      const { data, error } = await supabase
        .from('addon_suggestion_analytics')
        // One string literal, not a concatenation: PostgREST's generated types
        // only resolve the column list when it is statically analysable, and a
        // concatenated select silently degrades the result to an error type.
        .select('suggestion_type, period_start, period_end, total_shown, total_accepted, total_dismissed, acceptance_rate, dismissal_rate')
        .eq('shop_id', shopId)
        .order('period_start', { ascending: false })
        .limit(40);

      if (cancelled) return;
      setView(deriveAddonRuleQuality(
        (data ?? null) as AddonAnalyticsReadRow[] | null, Boolean(error)));
    })();

    return () => { cancelled = true; };
  }, [shopId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Are these suggestions asking the right question?</CardTitle>
        <CardDescription>
          How often each check was suggested on a vehicle, and what the shop decided.
          A check that is suggested constantly and declined constantly is usually a
          problem with the rule, not with the decision.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {view === null && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}

        {view?.state === 'unavailable' && (
          <p className="text-sm text-muted-foreground" data-testid="rule-quality-unavailable">
            Outcome data is unavailable right now. This is a read problem, not a
            result — nothing here should be taken as zero.
          </p>
        )}

        {view?.state === 'empty' && (
          <p className="text-sm text-muted-foreground" data-testid="rule-quality-empty">
            No outcome data yet. Suggestions are summarised once a day; this will
            fill in after suggestions have been shown on a completed day.
          </p>
        )}

        {view?.state === 'ready' && (
          <div className="space-y-4" data-testid="rule-quality-ready">
            <p className="text-xs text-muted-foreground" data-testid="rule-quality-period">
              Period: {formatPeriodRange(view.periodStart, view.periodEnd)}
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Check</th>
                    <th className="py-2 pr-4 font-medium text-right">Suggested</th>
                    <th className="py-2 pr-4 font-medium text-right">Added</th>
                    <th className="py-2 pr-4 font-medium text-right">Declined</th>
                    <th className="py-2 pr-4 font-medium text-right">Added %</th>
                    <th className="py-2 font-medium text-right">Declined %</th>
                  </tr>
                </thead>
                <tbody>
                  {view.families.map((f) => (
                    <tr
                      key={f.suggestionType}
                      className="border-b border-border/50"
                      data-testid={`rule-quality-row-${f.suggestionType}`}
                    >
                      <td className="py-2 pr-4 font-medium text-foreground">{f.label}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{f.shown}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{f.accepted}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{f.declined}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{f.acceptanceRate}%</td>
                      <td className="py-2 text-right tabular-nums">{f.declineRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">
              These are shop totals for the period. Declining a suggestion is a normal
              answer and is not recorded against anyone.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
